/*
 * 模块: ABAAnalysisDetail
 * 职责: 数据分析详情页
 * 依赖: useABAAnalysisData, 子组件
 * 导出: ABAAnalysisDetail
 * 限制: 文件<150行
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import {
  SKUDetailHeader,
  SKUInfoSection,
  DataAnalysisTabs,
} from '../components/SKUDetail';
import { AIConfigPanel } from '../components/SKUDetail/AIConfigPanel';
import { UserPreference } from '../types/ai-analysis-types';
import { AIConfigProvider } from '../contexts/AIConfigContext';
import { EmptyState } from '../components/UI/EmptyState';
import { useABAAnalysisData } from '../hooks/useABAAnalysisData';
import './SKUDetail.css';

/**
 * 数据分析详情页面
 */
export const ABAAnalysisDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [, setCurrentAIConfig] = useState<UserPreference | null>(null);

  // 使用整合的 Hook 获取所有数据
  const {
    loading,
    sku,
    periods,
    keywords,
    competitors,
    summary,
    reload,
    networkData,
    brandDetails,
    networkLoading,
    brandDetailsLoading,
    generateBrandNetwork,
    fetchBrandDetails,
    matrixData,
    rankRangeData,
    brandShiftData,
    matrixLoading,
    handleExport,
    handleExportEnhancedExcel,
    handleDelete,
    spPeriods,
    spSelectedPeriodId,
    advantageData,
    marketOverview,
    opportunityData,
    keywordPivotData,
    spLoading,
    handleTabChange
  } = useABAAnalysisData(id);

  // 跳转到数据管理
  const handleGoToDataManagement = () => {
    navigate(`/data-management/${id}`);
  };

  // 加载状态
  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  // SKU不存在
  if (!sku) {
    return (
      <EmptyState
        type="error"
        title="SKU不存在"
        description="未找到该SKU，可能已被删除或ID不正确"
        action={{
          label: '返回列表',
          onClick: () => navigate('/aba-analysis'),
        }}
      />
    );
  }

  // 没有周期数据时的提示
  if (periods.length === 0) {
    return (
      <div style={{ padding: '24px' }}>
        <EmptyState
          type="no-data"
          title="暂无数据"
          description={
            <>
              该SKU还没有上传数据，无法查看分析结果。
              <br />
              <span style={{ color: '#999', fontSize: 12 }}>
                支持上传：热门搜索词数据、搜索查询绩效数据
              </span>
            </>
          }
          action={{
            label: '前往上传数据',
            onClick: handleGoToDataManagement,
          }}
        />
      </div>
    );
  }

  return (
    <AIConfigProvider>
      <div className="sku-detail-page">
        <SKUDetailHeader
          skuId={id!}
          skuName={sku.name}
          onReload={reload}
          onDelete={handleDelete}
        />

        <SKUInfoSection
          sku={sku}
          periodsCount={periods.length}
          summary={summary ?? null}
        />

        {/* AI 配置面板 */}
        {sku.id && (
          <AIConfigPanel
            skuId={sku.id}
            skuName={sku.name}
            onConfigChange={setCurrentAIConfig}
          />
        )}

        <DataAnalysisTabs
          periodsCount={periods.length}
          keywords={keywords}
          competitors={competitors}
          networkData={networkData}
          brandDetails={brandDetails}
          networkLoading={networkLoading}
          brandDetailsLoading={brandDetailsLoading}
          matrixData={matrixData}
          rankRangeData={rankRangeData}
          brandShiftData={brandShiftData}
          matrixLoading={matrixLoading}
          onTabChange={handleTabChange}
          onExport={handleExport}
          onExportEnhanced={handleExportEnhancedExcel}
          onGenerateNetwork={generateBrandNetwork}
          onRefreshBrandDetails={fetchBrandDetails}
          // 搜索查询绩效数据
          spPeriodsCount={spPeriods.length}
          spSelectedPeriodId={spSelectedPeriodId}
          advantageData={advantageData}
          marketOverview={marketOverview}
          keywordPivotData={keywordPivotData}
          opportunityData={opportunityData}
          spLoading={spLoading}
        />
      </div>
    </AIConfigProvider>
  );
};

export default ABAAnalysisDetail;
